import { useEffect, useRef, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import EditIcon from "@mui/icons-material/Edit";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { LocationCard } from "./LocationCard";
import { formatProdNeededCode } from "../utils/prodNeeded";

export function ActiveItemPanel({
  activeItem,
  locations,
  activeLocationId,
  highlightedLocationId,
  editSignal,
  panelRef,
  onNewLocation,
  onSelectLocation,
  onDeleteLocation,
  onRenameItem,
  onOpenHistoricNames,
  onEditProdNeeded,
}) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(activeItem?.item_name || "");
  const [locationsCollapsed, setLocationsCollapsed] = useState(false);
  const inputRef = useRef(null);
  const lastEditSignalRef = useRef(0);
  const itemHasDroppedLocation = locations.some((location) => location.is_dropped);

  useEffect(() => {
    setDraftName(activeItem?.item_name || "");
    setEditing(false);
    setLocationsCollapsed(false);
  }, [activeItem?.id, activeItem?.item_name]);

  useEffect(() => {
    if (!activeItem || lastEditSignalRef.current === editSignal) {
      return;
    }
    lastEditSignalRef.current = editSignal;
    setEditing(true);
  }, [activeItem, editSignal]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const saveName = async () => {
    const nextName = draftName.trim();
    if (!activeItem) {
      return;
    }
    if (!nextName) {
      setDraftName(activeItem.item_name);
      setEditing(false);
      return;
    }
    if (nextName === activeItem.item_name) {
      setEditing(false);
      return;
    }
    const saved = await onRenameItem(nextName);
    if (saved) {
      setEditing(false);
    }
  };

  return (
    <section className="panel active-panel" ref={panelRef}>
      <div className="current-item-bar">
        <div className="current-item-title">
          <div className="round-icon small">
            <AssignmentTurnedInIcon />
          </div>
          <div className="current-name-block">
            <div className="eyebrow">Current Item</div>
            {editing ? (
              <input
                ref={inputRef}
                className="inline-name-input"
                value={draftName}
                maxLength="255"
                aria-label="Item name"
                onChange={(event) => setDraftName(event.target.value)}
                onBlur={saveName}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    saveName();
                  }
                  if (event.key === "Escape") {
                    setDraftName(activeItem.item_name);
                    setEditing(false);
                  }
                }}
              />
            ) : (
              <div className="headline compact-headline">
                <span>{activeItem.item_name}</span>
                <button
                  type="button"
                  className="mini-icon-button"
                  aria-label={`Edit ${activeItem.item_name}`}
                  onClick={() => setEditing(true)}
                >
                  <EditIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="mini-icon-button"
                  aria-label="Use historic product name"
                  onClick={onOpenHistoricNames}
                >
                  <HistoryIcon fontSize="small" />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="current-item-actions">
          <button
            className="touch-button prod-needed-button"
            type="button"
            onClick={onEditProdNeeded}
          >
            <Inventory2OutlinedIcon />
            PROD NEEDED {formatProdNeededCode(activeItem.prod_needed)}
          </button>
          <button
            className="touch-button dashed new-location-button"
            type="button"
            onClick={onNewLocation}
          >
            <AddCircleOutlineIcon /> NEW LOCATION
          </button>
        </div>
      </div>

      <button
        type="button"
        className="section-header collapsible-heading locations-heading"
        aria-expanded={!locationsCollapsed}
        onClick={() => setLocationsCollapsed((current) => !current)}
      >
        <span>
          <LocationOnIcon /> Locations
        </span>
        <span className="collapse-meta">
          {locations.length}
          {locationsCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
        </span>
      </button>

      {!locationsCollapsed && (
        locations.length === 0 ? (
          <div className="empty-state compact-empty">No locations</div>
        ) : (
          <div className="location-grid active-location-grid">
            {locations.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                selected={location.id === Number(activeLocationId)}
                highlighted={location.id === Number(highlightedLocationId)}
                compact
                hasDroppedSibling={itemHasDroppedLocation && !location.is_dropped}
                onClick={onSelectLocation}
                onDelete={onDeleteLocation}
              />
            ))}
          </div>
        )
      )}
    </section>
  );
}
